import { alpha, Box, Button, Stack, useTheme } from '@mui/material';
import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';
import RecordingTimer from './RecordingTimer';
import FeedbackDialog from 'components/FeedbackDialog';

interface RecordingControlsProps {
  isRecording: boolean;
  isFinished: boolean;
  isSubmitting: boolean;
  recordingTime?: number;
  onStopRecording: () => void;
  onStartRecording: () => Promise<void> | void;
  onSubmitRecording: () => void;
  isStopping: boolean;
}

const COUNTDOWN_START = 7;

const RecordingControls = ({
  isRecording,
  isFinished,
  isSubmitting,
  isStopping,
  onStopRecording,
  onStartRecording,
  onSubmitRecording,
  recordingTime
}: RecordingControlsProps) => {
  const [state, setState] = useState<'idle' | 'countdown' | 'starting' | 'recording'>('idle');

  const [countdown, setCountdown] = useState(COUNTDOWN_START);

  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const theme = useTheme();

  // --- старт таймера ---
  useEffect(() => {
    if (state !== 'countdown') return;

    if (countdown === 0) {
      setState('starting');
      return;
    }

    const t = setTimeout(() => {
      setCountdown((v) => v - 1);
    }, 1000);

    return () => clearTimeout(t);
  }, [state, countdown]);

  // --- запуск записи ---
  useEffect(() => {
    if (state !== 'starting') return;

    let cancelled = false;

    const start = async () => {
      try {
        await onStartRecording();
        if (!cancelled) {
          setState('recording');
        }
      } catch {
        if (!cancelled) {
          setState('idle');
          setCountdown(COUNTDOWN_START);
        }
      }
    };

    start();

    return () => {
      cancelled = true;
    };
  }, [state, onStartRecording]);

  // --- синхронизация снаружи ---
  useEffect(() => {
    if (isRecording) {
      setState('recording');
    }
  }, [isRecording]);

  useEffect(() => {
    if (!isFinished && state === 'idle') {
      setState('countdown');
      setCountdown(COUNTDOWN_START);
    }
  }, [isFinished, state]);

  // ================= UI =================

  if (isFinished) {
    return (
      <Stack spacing={3}>
        <Button
          fullWidth
          size="large"
          variant="contained"
          color="primary"
          onClick={onSubmitRecording}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Отправка...' : 'К следующему вопросу'}
        </Button>
      </Stack>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={2}>
        <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
        {state === 'recording' ? (
          <Button
            fullWidth
            size="large"
            variant="contained"
            color="error"
            onClick={onStopRecording}
            startIcon={<Icon icon="eva:stop-circle-fill" />}
            disabled={isStopping}
          >
            {isStopping ? (
              'Завершаем запись...'
            ) : recordingTime ? (
              <RecordingTimer textBefore="Завершить запись" recordingTime={recordingTime} />
            ) : (
              'Завершить запись'
            )}
          </Button>
        ) : (
          <Button fullWidth size="large" variant="contained" disabled>
            Запись начнётся через {countdown} сек.
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default RecordingControls;
